import { CoreModalDialog } from '@components'
import CoreLoader from '@components/CoreLoader'
import { useExpenseWorkflowCookie } from '@hooks'
import CreateCustomApprovalMatrixIcon from '@images/icons/create-matrix.svg?react'
import InviteYourTeamIcon from '@images/icons/invite-team.svg?react'
import SetUpDepartmentsIcon from '@images/icons/set-department.svg?react'

import { ReportEditModal } from '@views/expenseManagement/components'
import { paths, useDepartmentOptionsQry, useUserOptionsQry } from 'mmfintech-backend-api'
import { isMultiItemArray, isValidArray, tr } from 'mmfintech-commons'
import { useCallback, useEffect, useState } from 'react'
import WorkflowStep from './WorkflowStep'

import './expenseWorkflow.scss'

const ExpenseWorkflow = () => {
  const [createReportModalVisible, setCreateReportModalVisible] = useState<boolean>(false)

  const { onSeen } = useExpenseWorkflowCookie()

  const { users, usersLoading } = useUserOptionsQry()
  const { departments, departmentsLoading } = useDepartmentOptionsQry()

  const createModalOpen = useCallback(() => setCreateReportModalVisible(true), [])
  const createModalClose = useCallback(() => setCreateReportModalVisible(false), [])

  const getIsActionNeeded = useCallback(
    (stepId: string) => {
      switch (stepId) {
        case 'INVITE_YOUR_TEAM':
          return !isMultiItemArray(users)
        case 'SET_UP_DEPARTMENTS':
          return !isValidArray(departments)
        case 'SET_UP_APPROVAL_RULES':
          return !(isValidArray(departments) && departments.some(d => d.approvalMatrix))
      }
      return false
    },
    [users, departments]
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: set seen flag on mount
  useEffect(() => {
    onSeen()
  }, [])

  return (
    <>
      <div className='expense-workflow-wrapper'>
        {usersLoading || departmentsLoading ? (
          <CoreLoader />
        ) : (
          <>
            <div className='header'>
              <div className='title'>
                {tr('FRONTEND.EXPENSES.EXPENSE_WORKFLOW.TITLE', 'Get started by setting up your expense workflow.')}
              </div>
            </div>

            <div className='expense-workflow-container'>
              <div className='content'>
                <WorkflowStep
                  icon={<InviteYourTeamIcon />}
                  id='INVITE_YOUR_TEAM'
                  index={1}
                  title={tr('FRONTEND.EXPENSES.EXPENSE_WORKFLOW.INVITE_YOUR_TEAM', 'Invite Your Team')}
                  description={tr(
                    'FRONTEND.EXPENSES.EXPENSE_WORKFLOW.INVITE_YOUR_TEAM_DESCRIPTION',
                    'Add team members and assign roles as Administrators or Staff. Administrators have full access to funds, expense submission, and approvals, while Staff permissions can be customized by owners.'
                  )}
                  actionLink={`${paths.userDepartmentManagement()}/users`}
                  actionText={tr('FRONTEND.EXPENSES.EXPENSE_WORKFLOW.INVITE_USERS', 'Invite Users')}
                  isActionNeeded={getIsActionNeeded}
                  openCreateReportModal={createModalOpen}
                />

                <WorkflowStep
                  icon={<SetUpDepartmentsIcon />}
                  id='SET_UP_DEPARTMENTS'
                  index={2}
                  title={tr('FRONTEND.EXPENSES.EXPENSE_WORKFLOW.SET_UP_DEPARTMENTS', 'Set Up Departments (Optional)')}
                  description={tr(
                    'FRONTEND.EXPENSES.EXPENSE_WORKFLOW.SET_UP_DEPARTMENTS_DESCRIPTION',
                    'Organize expenses by department for better visibility and multi-level approvals. Adding users to departments is optional but enhances tracking and transparency.'
                  )}
                  actionLink={`${paths.userDepartmentManagement()}/departments`}
                  actionText={tr('FRONTEND.EXPENSES.EXPENSE_WORKFLOW.CREATE_DEPARTMENTS', 'Create Departments')}
                  isActionNeeded={getIsActionNeeded}
                  openCreateReportModal={createModalOpen}
                />

                <WorkflowStep
                  icon={<CreateCustomApprovalMatrixIcon />}
                  id='SET_UP_APPROVAL_RULES'
                  index={3}
                  title={tr(
                    'FRONTEND.EXPENSES.EXPENSE_WORKFLOW.SET_UP_APPROVAL_RULES',
                    'Create Custom Approval Matrix (Optional)'
                  )}
                  description={tr(
                    'FRONTEND.EXPENSES.EXPENSE_WORKFLOW.SET_UP_APPROVAL_RULES_DESCRIPTION',
                    'Set up an Approval Matrix to assign up to 5 approvers per department, with a Final Approver making the final decision. Optional thresholds allow approval of expenses up to a set amount.'
                  )}
                  actionLink={`${paths.userDepartmentManagement()}/departments`}
                  actionText={tr('FRONTEND.EXPENSES.EXPENSE_WORKFLOW.CREATE_MATRIX', 'Create Matrix')}
                  isActionNeeded={getIsActionNeeded}
                  openCreateReportModal={createModalOpen}
                />
              </div>
            </div>
          </>
        )}
      </div>

      <CoreModalDialog
        visible={createReportModalVisible}
        header={tr('FRONTEND.REPORTS.CREATE.CREATE_NEW_REPORT', 'Create New Report')}
        content={<ReportEditModal onClose={createModalClose} />}
        options={{ closeOnEscape: false, closeOnClickOutside: false }}
        onClose={createModalClose}
      />
    </>
  )
}

export default ExpenseWorkflow
